import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import socketService from '../services/socket';

// 초기 상태
const initialState = {
  // 플레이어 정보
  player: {
    id: null,
    name: '',
    isHost: false,
    isAlive: true,
    role: null,
    isReady: false,
  },
  
  // 방 정보
  room: {
    id: null,
    name: '',
    players: [],
    maxPlayers: 8,
    isGameStarted: false,
    gamePhase: 'waiting', // waiting, discussion, voting, night, gameOver
    currentRound: 0,
  },
  
  // 게임 정보
  game: {
    sessionId: null,
    simulation: null,
    mode: null,
    llmResponse: null,
    isProcessing: false,
    timer: null,
    votes: {},
    nightActions: {},
    discussionMessages: [],
    gameLog: [],
  },
  
  // 방 목록
  roomsList: [],
  
  // UI 상태
  ui: {
    isLoading: false,
    error: null,
    isConnected: false,
  }
};

// 액션 타입
export const actionTypes = {
  // 플레이어 관련
  SET_PLAYER: 'SET_PLAYER',
  UPDATE_PLAYER: 'UPDATE_PLAYER',
  SET_PLAYER_READY: 'SET_PLAYER_READY',
  
  // 방 관련
  SET_ROOM: 'SET_ROOM',
  UPDATE_ROOM: 'UPDATE_ROOM',
  ADD_PLAYER_TO_ROOM: 'ADD_PLAYER_TO_ROOM',
  REMOVE_PLAYER_FROM_ROOM: 'REMOVE_PLAYER_FROM_ROOM',
  UPDATE_PLAYER_IN_ROOM: 'UPDATE_PLAYER_IN_ROOM',
  SET_ROOMS_LIST: 'SET_ROOMS_LIST',
  
  // 게임 관련
  START_GAME: 'START_GAME',
  SET_GAME_PHASE: 'SET_GAME_PHASE',
  SET_TIMER: 'SET_TIMER',
  ADD_VOTE: 'ADD_VOTE',
  ADD_NIGHT_ACTION: 'ADD_NIGHT_ACTION',
  ADD_DISCUSSION_MESSAGE: 'ADD_DISCUSSION_MESSAGE',
  ADD_GAME_LOG: 'ADD_GAME_LOG',
  SET_GAME_SESSION: 'SET_GAME_SESSION',
  SET_GAME_MODE: 'SET_GAME_MODE',
  SET_SIMULATION: 'SET_SIMULATION',
  SET_LLM_RESPONSE: 'SET_LLM_RESPONSE',
  SET_PROCESSING: 'SET_PROCESSING',
  
  // UI 관련
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// 리듀서 함수
const gameReducer = (state, action) => {
  switch (action.type) {
    // 플레이어 관련
    case actionTypes.SET_PLAYER:
      return {
        ...state,
        player: { ...state.player, ...action.payload }
      };
    
    case actionTypes.UPDATE_PLAYER:
      return {
        ...state,
        player: { ...state.player, ...action.payload }
      };
    
    case actionTypes.SET_PLAYER_READY:
      return {
        ...state,
        player: { ...state.player, isReady: action.payload }
      };
    
    // 방 관련
    case actionTypes.SET_ROOM:
      return {
        ...state,
        room: { ...state.room, ...action.payload }
      };
    
    case actionTypes.UPDATE_ROOM:
      return {
        ...state,
        room: { ...state.room, ...action.payload }
      };
    
    case actionTypes.ADD_PLAYER_TO_ROOM:
      return {
        ...state,
        room: {
          ...state.room,
          players: [...state.room.players, action.payload]
        }
      };
    
    case actionTypes.REMOVE_PLAYER_FROM_ROOM:
      return {
        ...state,
        room: {
          ...state.room,
          players: state.room.players.filter(player => player.id !== action.payload)
        }
      };
    
    case actionTypes.UPDATE_PLAYER_IN_ROOM:
      return {
        ...state,
        room: {
          ...state.room,
          players: state.room.players.map(player =>
            player.id === action.payload.playerId
              ? { ...player, ...action.payload.updates }
              : player
          )
        }
      };
    
    case actionTypes.SET_ROOMS_LIST:
      return {
        ...state,
        roomsList: action.payload
      };
    
    // 게임 관련
    case actionTypes.START_GAME:
      return {
        ...state,
        room: {
          ...state.room,
          isGameStarted: true,
          gamePhase: 'discussion',
          currentRound: 1
        }
      };
    
    case actionTypes.SET_GAME_PHASE:
      return {
        ...state,
        room: {
          ...state.room,
          gamePhase: action.payload
        }
      };
    
    case actionTypes.SET_TIMER:
      return {
        ...state,
        game: {
          ...state.game,
          timer: action.payload
        }
      };
    
    case actionTypes.ADD_VOTE:
      return {
        ...state,
        game: {
          ...state.game,
          votes: {
            ...state.game.votes,
            [action.payload.voterId]: action.payload.targetId
          }
        }
      };
    
    case actionTypes.ADD_NIGHT_ACTION:
      return {
        ...state,
        game: {
          ...state.game,
          nightActions: {
            ...state.game.nightActions,
            [action.payload.playerId]: action.payload.action
          }
        }
      };
    
    case actionTypes.ADD_DISCUSSION_MESSAGE:
      return {
        ...state,
        game: {
          ...state.game,
          discussionMessages: [
            ...state.game.discussionMessages,
            action.payload
          ]
        }
      };
    
    case actionTypes.ADD_GAME_LOG:
      return {
        ...state,
        game: {
          ...state.game,
          gameLog: [
            ...state.game.gameLog,
            action.payload
          ]
        }
      };
    
    // 게임 관련
    case actionTypes.SET_GAME_SESSION:
      return {
        ...state,
        game: {
          ...state.game,
          sessionId: action.payload
        }
      };
    
    case actionTypes.SET_GAME_MODE:
      return {
        ...state,
        game: {
          ...state.game,
          mode: action.payload
        }
      };
    
    case actionTypes.SET_SIMULATION:
      return {
        ...state,
        game: {
          ...state.game,
          simulation: action.payload
        }
      };
    
    case actionTypes.SET_LLM_RESPONSE:
      return {
        ...state,
        game: {
          ...state.game,
          llmResponse: action.payload
        }
      };
    
    case actionTypes.SET_PROCESSING:
      return {
        ...state,
        game: {
          ...state.game,
          isProcessing: action.payload
        }
      };
    
    // UI 관련
    case actionTypes.SET_LOADING:
      return {
        ...state,
        ui: {
          ...state.ui,
          isLoading: action.payload
        }
      };
    
    case actionTypes.SET_ERROR:
      return {
        ...state,
        ui: {
          ...state.ui,
          error: action.payload
        }
      };
    
    case actionTypes.SET_CONNECTION_STATUS:
      return {
        ...state,
        ui: {
          ...state.ui,
          isConnected: action.payload
        }
      };
    
    case actionTypes.CLEAR_ERROR:
      return {
        ...state,
        ui: {
          ...state.ui,
          error: null
        }
      };
    
    default:
      return state;
  }
};

// Context 생성
const GameContext = createContext();

// Provider 컴포넌트
export const GameProvider = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  
  // 소켓 연결 및 이벤트 리스너 설정
  useEffect(() => {
    const socket = socketService.connect();
    
    // 연결 상태 업데이트
    socket.on('connect', () => {
      dispatch({ type: actionTypes.SET_CONNECTION_STATUS, payload: true });
      
      // 로컬 스토리지에서 플레이어 정보 복원
      const savedPlayerName = localStorage.getItem('playerName');
      if (savedPlayerName) {
        dispatch({ type: actionTypes.SET_PLAYER, payload: { name: savedPlayerName } });
      }
      
      // 연결 성공 시 방 목록 자동 조회
      socketService.getRooms();
    });
    
    socket.on('disconnect', () => {
      dispatch({ type: actionTypes.SET_CONNECTION_STATUS, payload: false });
      dispatch({ type: actionTypes.SET_ERROR, payload: '서버 연결이 끊어졌습니다. 재연결을 시도합니다...' });
    });
    
    // 재연결 시도
    socket.on('reconnect', () => {
      dispatch({ type: actionTypes.SET_CONNECTION_STATUS, payload: true });
      dispatch({ type: actionTypes.CLEAR_ERROR });
      // 재연결 성공 시 방 목록 다시 조회
      socketService.getRooms();
      
      // 현재 방에 있었다면 방 정보 다시 조회
      if (state.room.id) {
        socketService.getRoomInfo(state.room.id);
      }
    });
    
    // 방 관련 이벤트 (백엔드 이벤트 이름과 일치)
    socket.on('room-created', (data) => {
      if (data.success) {
        dispatch({ type: actionTypes.SET_ROOM, payload: data.room });
        dispatch({ type: actionTypes.SET_PLAYER, payload: {
          id: data.player.id,
          name: data.player.name,
          isHost: data.player.isHost
        }});
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      } else {
        dispatch({ type: actionTypes.SET_ERROR, payload: data.message });
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    });
    
    socket.on('room-joined', (data) => {
      if (data.success) {
        console.log('Room joined data:', data);
        dispatch({ type: actionTypes.SET_ROOM, payload: data.room });
        dispatch({ type: actionTypes.SET_PLAYER, payload: {
          id: data.player.id,
          name: data.player.name,
          isHost: data.player.isHost
        }});
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      } else {
        dispatch({ type: actionTypes.SET_ERROR, payload: data.message });
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    });
    
    socket.on('player-joined', (data) => {
      if (data.success) {
        dispatch({ type: actionTypes.ADD_PLAYER_TO_ROOM, payload: data.player });
        dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
        console.log('Player joined:', data.player);
      }
    });
    
    socket.on('player-left', (data) => {
      if (data.success) {
        dispatch({ type: actionTypes.REMOVE_PLAYER_FROM_ROOM, payload: data.player.id });
        dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
        
        // 방장이 변경된 경우
        if (data.newHost) {
          dispatch({ type: actionTypes.UPDATE_PLAYER, payload: { isHost: data.newHost.id === state.player.id } });
          // 방의 모든 플레이어 정보 업데이트
          dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
        }
      }
    });
    
    socket.on('room-left', (data) => {
      if (data.success) {
        dispatch({ type: actionTypes.SET_ROOM, payload: initialState.room });
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    });
    
    // 방 목록 관련 이벤트
    socket.on('rooms-list', (data) => {
      dispatch({ type: actionTypes.SET_ROOMS_LIST, payload: data.rooms });
    });
    
    socket.on('rooms-list-updated', (data) => {
      dispatch({ type: actionTypes.SET_ROOMS_LIST, payload: data.rooms });
    });
    
    socket.on('room-info-response', (data) => {
      dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
    });
    
    socket.on('room-updated', (data) => {
      if (data.success) {
        dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
      }
    });
    
    // 플레이어 준비 상태 변경 이벤트
    socket.on('player-ready-changed', (data) => {
      if (data.success) {
        dispatch({
          type: actionTypes.UPDATE_PLAYER_IN_ROOM,
          payload: {
            playerId: data.playerId,
            updates: { isReady: data.isReady }
          }
        });
        
        // 현재 플레이어의 준비 상태도 업데이트
        if (data.playerId === state.player.id) {
          dispatch({ type: actionTypes.SET_PLAYER_READY, payload: data.isReady });
        }
        
        dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
      }
    });
    
    // 게임 시작 자격 변경 이벤트
    socket.on('game-start-eligibility', (data) => {
      if (data.success) {
        dispatch({
          type: actionTypes.UPDATE_ROOM,
          payload: {
            canStartGame: data.canStartGame,
            // 다른 방 정보는 유지
            ...data.room
          }
        });
      }
    });
    
    // 게임 관련 이벤트
    socket.on('game-started', (data) => {
      if (data.success) {
        dispatch({ type: actionTypes.START_GAME });
        dispatch({ type: actionTypes.SET_GAME_SESSION, payload: data.sessionId });
        dispatch({ type: actionTypes.SET_GAME_MODE, payload: data.mode || null });
        dispatch({ type: actionTypes.SET_SIMULATION, payload: data.simulation });
        dispatch({ type: actionTypes.UPDATE_PLAYER, payload: { role: data.role, isReady: false } });
        
        // 방 정보도 업데이트
        dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
      }
    });
    
    socket.on('game-phase-changed', (phaseData) => {
      dispatch({ type: actionTypes.SET_GAME_PHASE, payload: phaseData.phase });
      if (phaseData.timer) {
        dispatch({ type: actionTypes.SET_TIMER, payload: phaseData.timer });
      }
    });
    
    socket.on('vote-received', (voteData) => {
      dispatch({ type: actionTypes.ADD_VOTE, payload: voteData });
    });
    
    socket.on('discussion-message', (messageData) => {
      dispatch({ type: actionTypes.ADD_DISCUSSION_MESSAGE, payload: messageData });
    });
    
    socket.on('game-log', (logData) => {
      dispatch({ type: actionTypes.ADD_GAME_LOG, payload: logData });
    });
    
    // 새 게임 준비 이벤트
    socket.on('new-game-ready', (data) => {
      if (data.success) {
        dispatch({ type: actionTypes.UPDATE_ROOM, payload: data.room });
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
        
        // 게임 관련 상태 초기화
        dispatch({ type: actionTypes.SET_GAME_SESSION, payload: null });
        dispatch({ type: actionTypes.SET_GAME_MODE, payload: null });
        dispatch({ type: actionTypes.SET_SIMULATION, payload: null });
        dispatch({ type: actionTypes.SET_LLM_RESPONSE, payload: null });
        dispatch({ type: actionTypes.SET_PROCESSING, payload: false });
      }
    });
    
    // 게임 완료 이벤트
    socket.on('game-finished', (data) => {
      if (data.success) {
        // 명시적으로 isGameStarted를 false로 설정
        const roomData = {
          ...data.room,
          isGameStarted: false
        };
        
        dispatch({ type: actionTypes.UPDATE_ROOM, payload: roomData });
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
        
        // 게임 관련 상태 초기화
        dispatch({ type: actionTypes.SET_GAME_SESSION, payload: null });
        dispatch({ type: actionTypes.SET_GAME_MODE, payload: null });
        dispatch({ type: actionTypes.SET_SIMULATION, payload: null });
        dispatch({ type: actionTypes.SET_LLM_RESPONSE, payload: null });
        dispatch({ type: actionTypes.SET_PROCESSING, payload: false });
      }
    });
    
    // 에러 처리
    socket.on('error', (error) => {
      dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
    });
    
    return () => {
      socketService.disconnect();
    };
  }, []);
  
  // 액션 생성자 함수들
  const actions = useMemo(() => ({
    // 플레이어 관련
    setPlayer: (playerData) => {
      dispatch({ type: actionTypes.SET_PLAYER, payload: playerData });
      // 플레이어 이름을 로컬 스토리지에 저장
      if (playerData.name) {
        localStorage.setItem('playerName', playerData.name);
      }
    },
    
    updatePlayer: (playerData) => {
      dispatch({ type: actionTypes.UPDATE_PLAYER, payload: playerData });
    },
    
    toggleReady: () => {
      // 서버에 준비 상태 변경 알림만 보내고, 서버 응답을 기다려서 상태 업데이트
      socketService.toggleReady(state.room.id, state.player.id);
    },
    
    // 방 관련
    createRoom: (roomData) => {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      socketService.createRoom({
        playerName: state.player.name,
        roomName: roomData.name,
        isPrivate: roomData.isPrivate || false,
        password: roomData.password || ''
      });
    },
    
    joinRoom: (roomId, playerData) => {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      socketService.joinRoom(roomId, playerData);
    },
    
    leaveRoom: () => {
      if (state.room.id) {
        socketService.leaveRoom(state.room.id);
      }
      dispatch({ type: actionTypes.SET_ROOM, payload: initialState.room });
    },
    
    getRooms: () => {
      socketService.getRooms();
    },
    
    getRoomInfo: (roomId) => {
      socketService.getRoomInfo(roomId);
    },
    
    // 게임 관련
    startGame: () => {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      socketService.startGame(state.room.id);
    },
    
    requestNewGame: (roomId) => {
      socketService.requestNewGame(roomId);
    },
    
    gameCompleted: (roomId) => {
      socketService.gameCompleted(roomId);
    },
    
    submitAnswer: (roomId, sessionId, answer) => {
      socketService.submitAnswer(roomId, sessionId, answer);
    },
    
    getGameResult: (sessionId) => {
      socketService.getGameResult(sessionId);
    },
    
    playerAction: (actionData) => {
      socketService.playerAction(actionData);
    },
    
    getSocket: () => {
      return socketService.getSocket();
    },
    
    // UI 관련
    setLoading: (isLoading) => {
      dispatch({ type: actionTypes.SET_LOADING, payload: isLoading });
    },
    
    setError: (error) => {
      dispatch({ type: actionTypes.SET_ERROR, payload: error });
    },
    
    clearError: () => {
      dispatch({ type: actionTypes.CLEAR_ERROR });
    }
  }), [state.room.id, state.player.id, state.player.name]);
  
  const value = {
    state,
    dispatch,
    actions
  };
  
  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

// Context를 사용하는 커스텀 훅
export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export default GameContext;
